import { useDispatch, useSelector } from 'react-redux';

import {
  getIsMergePending,
  openDialog,
} from 'src/redux/pull-request/merge-reducer';
import { getIsAsyncMergeInProgress } from 'src/redux/pull-request/selectors';
import { getIsCustomMergeChecksEnabled } from 'src/selectors/global-selectors';

import { useClassicMergeData } from './merge-data-classic';
import { useCustomMergeData } from './merge-data-custom';
import { CombinedMergeData } from './types';

/**
 * A hook which returns data related to merging of the PR such as whether the PR is able to be merged, fail count of on
 * going merge checks, etc which is used for manipulating UI behaviour such as enabling/disabling the merge button,
 * messaging in the merge dialog, etc.
 *
 * There are 2 types of merge checks which are:
 * 1. classic merge checks - handled in Core which are configured in the "Branch restrictions" repo admin section
 * 2. custom merge checks - handled via Forge and bbc-checks service which are configured in the "Merge checks" repo
 * admin section
 *
 * If custom merge checks are enabled, we need to combine the results from classic merge checks (querying handled by and
 * stored in Redux) and custom merge checks (handled by react-resource-router) to arrive at the correct status which this
 * hook does. If the FF for custom merge checks are disabled simply only return the status based on classic merge checks.
 */
export const useMergeData = (): CombinedMergeData => {
  const forgeMergeChecksEnabled = useSelector(getIsCustomMergeChecksEnabled);

  const {
    classicPullRequestIsMergeable,
    classicPullRequestMergeChecksIsMergeable,
    classicCanCreatePendingMerge,
    classicChecksFailedCount,
    classicChecksTotalCount,
  } = useClassicMergeData();
  const {
    customMergeChecksIsMergeable,
    forgeMergeChecksData,
    customMergeChecksError,
    requiredCustomOnMergeChecksFailedCount,
  } = useCustomMergeData();

  const isMergePending = useSelector(getIsMergePending);
  const isAsyncMergeInProgress = useSelector(getIsAsyncMergeInProgress);
  const dispatch = useDispatch();
  const onClick = () => dispatch(openDialog());

  // data that is the same regardless of Forge merge checks being enabled or not
  const uniformMergeData = {
    isMergePending,
    isAsyncMergeInProgress,
    onClick,
  };

  if (!forgeMergeChecksEnabled) {
    return {
      ...uniformMergeData,
      mergeChecksIsMergeable: classicPullRequestMergeChecksIsMergeable,
      canCreatePendingMerge: classicCanCreatePendingMerge,
      countFailedChecks: classicChecksFailedCount,
      canShowAllChecksPassedNotification:
        classicChecksTotalCount > 0 && classicPullRequestIsMergeable,
      forgeMergeChecksData: null,
      mergeChecksLoading: false,
      classicChecksCount: classicChecksTotalCount,
      customMergeChecksError: null,
      requiredCustomOnMergeChecksFailedCount: 0,
    };
  }

  const combinedMergeChecksIsMergeable =
    customMergeChecksIsMergeable && classicPullRequestMergeChecksIsMergeable;

  const combinedCanCreatePendingMerge =
    customMergeChecksIsMergeable && classicCanCreatePendingMerge;

  const combinedCountFailedChecks =
    classicChecksFailedCount + forgeMergeChecksData.failedPreMergeChecksCount;

  const onMergeChecksCount =
    forgeMergeChecksData.enabledOnMergeChecks.enabledChecks?.length ?? 0;

  const combinedCanShowAllChecksPassedNotification =
    classicChecksTotalCount +
      forgeMergeChecksData.preMergeChecksCount +
      onMergeChecksCount >
      0 &&
    classicPullRequestIsMergeable &&
    customMergeChecksIsMergeable;

  return {
    ...uniformMergeData,
    mergeChecksIsMergeable: combinedMergeChecksIsMergeable,
    canCreatePendingMerge: combinedCanCreatePendingMerge,
    countFailedChecks: combinedCountFailedChecks,
    canShowAllChecksPassedNotification:
      combinedCanShowAllChecksPassedNotification,
    mergeChecksLoading: forgeMergeChecksData.mergeChecksResultsLoading,
    classicChecksCount: classicChecksTotalCount,
    forgeMergeChecksData,
    customMergeChecksError,
    requiredCustomOnMergeChecksFailedCount,
  };
};
